import React, { useEffect, useState } from "react";
import "./TakePayments.css";
import axiosInstance from "../../../helpers/axiosInstance";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../../hooks/useAuth";
import { useSearchParams } from "react-router-dom";


function TakePayments() {
    const [invoices, setInvoices] = useState({});
    // Opened from an invoice, the member comes with it, as in the app.
    const [searchParams] = useSearchParams();
    const [selectedMember, setSelectedMember] = useState(searchParams.get("member"));
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [updatedTotal, setUpdatedTotal] = useState(null);
    const [customAmount, setCustomAmount] = useState('');
    const [membersList, setMembersList] = useState([]);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [showDetails, setShowDetails] = useState(false);
    // A late payment penalty can be taken from the member's savings balance
    // instead of a payment: "savings" or "payment".
    const [source, setSource] = useState("payment");
    const isPenalty = invoiceLines.length > 0 && invoiceLines.every((line) => line.line_type === "penalty");
    const fromSavings = isPenalty && source === "savings";
    const { organisation, auth } = useAuth();
    

    useEffect(() => {
        if (selectedMember) {
            fetchInvoices();
            fetchMemberList();
        }
    }, [selectedMember, organisation._id]);

    const fetchMemberList = async () => {
        axiosInstance
        .get(`/api/member/${organisation._id}/getallminimalmembers`)
        .then((response) => {
          setMembersList(response.data);
          console.log(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    // Call fetchMemberList when component mounts
    useEffect(() => {
        fetchMemberList();
    }, [organisation._id]);

    const fetchInvoices = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/invoice/${organisation._id}/member/${selectedMember}`
            );
            setInvoices(response.data);
            setInvoiceLines(response.data.invoiceLines || []);
            setSelectedInvoice(response.data._id);
            setUpdatedTotal(response.data.total);
            // Also fetch invoice lines for the selected invoice
            
        } catch (error) {
            console.error(error);
        }
    };

    const handleMemberChange = (event) => {
        setSelectedMember(event.target.value);
    };

    const handleInvoiceChange = (event) => {
        setSelectedInvoice(event.target.value);
    };

    const handlePaymentMethodChange = (event) => {
        const method = event.target.value;
        setSelectedPaymentMethod(method);
         // Reset custom amount when changing payment method
        
        // Calculate any additional charges or discounts based on payment method
        const currentTotal = invoiceLines.reduce((acc, line) => acc + (line.total_amount || 0), 0);
        setCustomAmount(currentTotal);
        // Example: Add 2% convenience fee for UPI payments
        if (method === 'upi') {
            setUpdatedTotal({
                total: currentTotal
            });
        } else {
            // For cash, no additional charges
            setUpdatedTotal({
                total: currentTotal
            });
        }
    };

    const handleCustomAmountChange = (e) => {
        const value = e.target.value;
        // Allow only numbers and one decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setCustomAmount(parseInt(value) || 0);
            
            if (value) {
                const amount = parseFloat(value);
                if (!isNaN(amount)) {
                    setUpdatedTotal(prev => ({
                        ...prev,
                        total: amount,
                        isCustom: true
                    }));
                }
            } else {
                // Reset to calculated total if custom amount is cleared
                const currentTotal = invoiceLines.reduce((acc, line) => acc + (line.total_amount || 0), 0);
                setUpdatedTotal({
                    total: currentTotal,
                    isCustom: false
                });
            }
        }
    };

    const handlePayment = async () => {
        try {
            const paymentAmount = customAmount !== 0 
                ? parseFloat(customAmount) 
                : updatedTotal?.total || invoiceLines.reduce((acc, line) => acc + (line.total_amount || 0), 0);
                
            const data = {
                invoiceId: selectedInvoice,
                method: fromSavings ? "SAVINGS" : selectedPaymentMethod,
                amount: paymentAmount,
                isPartialPayment: customAmount !== 0 && paymentAmount < (updatedTotal?.total || 0),
                ...(fromSavings ? { source: "savings" } : {}),
            };
            const response = await axiosInstance.post(
                `/api/payment/${organisation._id}/payments/${selectedMember}`,
                data
            );
            toast(response.data?.message || "Payment processed successfully!");
            setSelectedMember('');
            setSelectedInvoice('');
            setSelectedPaymentMethod('');
            setCustomAmount(0);
            setUpdatedTotal(null);
            setInvoiceLines([]);
            setInvoices(null);
            setSource("payment");
        } catch (error) {
            console.error(error);
            toast(error.response?.data?.message || "Error processing payment");
        }
    };

    return (
        <div className="take-payments-container">
            <div className="take-payments-header">
                <h3 className="take-payments-heading">Take Payment</h3>
            </div>
            <div className="take-payments-member-select">
                <label htmlFor="member">Select Member:</label>
                <select
                    id="member"
                    value={selectedMember || ""}
                    onChange={handleMemberChange}
                    className="take-payments-select"
                >
                    <option value="">Select Member</option>
                    {membersList.map((member) => (
                        <option key={member._id} value={member._id}>
                            {member.full_name} 
                        </option>
                    ))}
                </select>
            </div>
            {selectedMember && (
                <div className="take-payments-invoice-table">
                    {!invoices?._id ? (
                        <p>No invoices available for this member.</p>
                    ) : (
                        <table className="take-payments-table">
                            <thead>
                                <tr>
                                    <th>Invoice Number</th>
                                    <th>Status</th>
                                    <th>Due Date</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                
                                    <tr key={invoices.id} onClick={() => setSelectedInvoice(invoices.id)}>
                                        <td>{invoices.invoice_number}</td>
                                        <td>{invoices?.status?.charAt(0)?.toUpperCase() + invoices?.status?.slice(1)}</td>
                                        <td>{new Date(invoices.due_date).toLocaleDateString("en-us", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}</td>
                                        <td>{parseFloat(invoices.total_amount).toFixed(2)}</td>
                                    </tr>
                                
                            </tbody>
                        </table>
                    )}

                    {invoiceLines.length > 0 && (
                        <div className="invoice-lines-container">
                            <h3 className="invoice-lines-title">Invoice Items</h3>
                            <table className="invoice-lines-table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Quantity</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceLines.map((line) => (
                                        <React.Fragment key={line._id}>
                                            <tr 
                                                className="take-payments-collapsible-row"
                                                onClick={() => setShowDetails(showDetails === line._id ? null : line._id)}
                                            >
                                                <td>
                                                    <div className="item-name">{line.item_name || 'N/A'}</div>
                                                    {line.item_description && (
                                                        <div className="item-description">{line.item_description}</div>
                                                    )}
                                                </td>
                                                <td>{line.item_quantity || 1}</td>
                                                <td>{(line.total_amount || 0).toFixed(2)}</td>
                                            </tr>
                                            {showDetails === line._id && (
                                                <tr className="take-payments-collapsible-content">
                                                    <td colSpan="4">
                                                        <table className="details-table">
                                                            <tbody>
                                                                <tr>
                                                                    <td>Description</td>
                                                                    <td>{line.item_description || 'N/A'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Price</td>
                                                                    <td>{(line.item_price || 0).toFixed(2)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Quantity</td>
                                                                    <td>{line.item_quantity || 1}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Interest</td>
                                                                    <td>{(line.item_interest || 0).toFixed(2)}</td>
                                                                </tr>
                                                                {line.item_notes && (
                                                                    <tr>
                                                                        <td>Notes</td>
                                                                        <td>{line.item_notes}</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="subtotal-row">
                                        <td colSpan="2" className="text-right">Subtotal (Price × Qty):</td>
                                        <td className="amount">
                                            {invoiceLines.reduce((acc, line) => acc + (line.item_price * (line.item_quantity || 1) || 0), 0).toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr className="interest-row">
                                        <td colSpan="2" className="text-right">Total Interest:</td>
                                        <td className="amount">
                                            {invoiceLines.reduce((acc, line) => acc + (line.item_interest * (line.item_quantity || 1) || 0), 0).toFixed(2)}
                                        </td>
                                    </tr>
                                    {updatedTotal?.convenienceFee > 0 && (
                                        <tr className="fee-row">
                                            <td colSpan="2" className="text-right">Convenience Fee (2%):</td>
                                            <td className="amount">+{updatedTotal.convenienceFee.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    <tr className="total-row">
                                        <td colSpan="2" className="text-right">Total Amount:</td>
                                        <td className="total-amount">
                                            {updatedTotal ? updatedTotal.total.toFixed(2) : (invoiceLines.reduce((acc, line) => acc + (line.total_amount || 0), 0) || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                    {invoices?._id && (
                        <div className="take-payments-payment-form">
                            {isPenalty ? (
                                <>
                                    <div className="payment-method-title">Take the penalty from:</div>
                                    <div className="payment-methods">
                                        {[
                                            ["savings", "Balance Savings", "Debited from their savings, and a penalty voucher"],
                                            ["payment", "Proceed Payment", "Recorded as a payment, and a penalty voucher"],
                                        ].map(([value, label, hint]) => (
                                            <label key={value} className="payment-method-option" title={hint}>
                                                <input
                                                    type="radio"
                                                    name="penalty-source"
                                                    value={value}
                                                    checked={source === value}
                                                    onChange={() => {
                                                        setSource(value);
                                                        // The amount is otherwise set by picking a method, which
                                                        // Balance Savings has none of.
                                                        setCustomAmount(invoiceLines.reduce((acc, line) => acc + (line.total_amount || 0), 0));
                                                    }}
                                                    className="payment-method-radio"
                                                />
                                                <span className="payment-method-label">
                                                    <span className="payment-method-name">{label}</span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                            {fromSavings ? null : (
                            <>
                            <div className="payment-method-title">Select Payment Method:</div>
                            <div className="payment-methods">
                                <label className="payment-method-option">
                                    <input
                                        type="radio"
                                        name="payment-method"
                                        value="UPI"
                                        checked={selectedPaymentMethod === 'UPI'}
                                        onChange={handlePaymentMethodChange}
                                        className="payment-method-radio"
                                    />
                                    <span className="payment-method-label">
                                        <span className="payment-method-name">UPI</span>
                                        <span className="payment-method-icon">💳</span>
                                    </span>
                                </label>
                                <label className="payment-method-option">
                                    <input
                                        type="radio"
                                        name="payment-method"
                                        value="CASH"
                                        checked={selectedPaymentMethod === 'CASH'}
                                        onChange={handlePaymentMethodChange}
                                        className="payment-method-radio"
                                    />
                                    <span className="payment-method-label">
                                        <span className="payment-method-name">Cash</span>
                                        <span className="payment-method-icon">💵</span>
                                    </span>
                                </label>
                                 <label className="payment-method-option">
                                    <input
                                        type="radio"
                                        name="payment-method"
                                        value="CHEQUE"
                                        checked={selectedPaymentMethod === 'CHEQUE'}
                                        onChange={handlePaymentMethodChange}
                                        className="payment-method-radio"
                                    />
                                    <span className="payment-method-label">
                                        <span className="payment-method-name">Cheque</span>
                                        <span className="payment-method-icon">💵</span>
                                    </span>
                                </label>
                                 <label className="payment-method-option">
                                    <input
                                        type="radio"
                                        name="payment-method"
                                        value="BANK"
                                        checked={selectedPaymentMethod === 'BANK'}
                                        onChange={handlePaymentMethodChange}
                                        className="payment-method-radio"
                                    />
                                    <span className="payment-method-label">
                                        <span className="payment-method-name">Bank</span>
                                        <span className="payment-method-icon">💵</span>
                                    </span>
                                </label>
                            </div>
                            </>
                            )}
                            
                            {/* {selectedPaymentMethod && (
                                <div className="custom-amount-container">
                                    <label htmlFor="custom-amount" className="custom-amount-label">
                                        Custom Amount (Optional):
                                    </label>
                                    <div className="custom-amount-input-group">
                                        <span className="currency-symbol">₹</span>
                                        <input
                                            type="text"
                                            id="custom-amount"
                                            value={customAmount}
                                            onChange={handleCustomAmountChange}
                                            placeholder={`Max: ${updatedTotal?.subtotal?.toFixed(2) || '0.00'}`}
                                            className="custom-amount-input"
                                        />
                                        {customAmount && (
                                            <button 
                                                type="button" 
                                                className="clear-amount-btn"
                                                onClick={() => {
                                                    setCustomAmount(0);
                                                    setUpdatedTotal(prev => ({
                                                        ...prev,
                                                        isCustom: false
                                                    }));
                                                }}
                                                title="Clear amount"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    <div className="amount-hint">
                                        {customAmount && updatedTotal?.isCustom && (
                                            <span className="custom-amount-note">
                                                {parseFloat(customAmount) > updatedTotal.total ? 
                                                    'Amount exceeds total due' :
                                                    'Custom amount set'
                                                }
                                            </span>
                                        )}
                                        {!customAmount && 'Leave empty to pay full amount'}
                                    </div>
                                </div>
                            )} */}
                            
                            <button 
                                onClick={handlePayment} 
                                className="pay-button"
                                disabled={(!fromSavings && !selectedPaymentMethod) || 
                                    (customAmount && 
                                     (isNaN(parseFloat(customAmount)) || 
                                      parseFloat(customAmount) <= 0))
                                }
                            >
                                {fromSavings ? 'Take from savings' : customAmount ? 'Pay Custom Amount' : 
                                 `Pay ${selectedPaymentMethod === 'upi' ? 'via UPI' : 'with Cash'}`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TakePayments;